// Las fotos y archivos de un chat (13-10, Pau: «como en WhatsApp o Telegram: ver todos los archivos y las fotos que se
// han mandado; poder ver si se le ha enviado alguna foto anteriormente»). En la ficha, una rejilla de 3×2 con lo último
// y «Ver todo»; la vista entera, con filtro de quién lo mandó. Sale de toda la conversación (servidor), no de lo cargado.
//
// 04-11-2026 (propuesta-2/fuentes/pdf.md §3): los documentos van en la MISMA rejilla que las fotos —un PDF con su
// portada, un informe con su título— y una etiqueta «PDF» / «Informe»; antes eran un icono genérico con el nombre de
// archivo, y la ficha decía «2 documentos, sin fotos». Todo se abre en la hoja de vista previa.
import { ChevronLeft, ImageOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { oficinaApi } from '../api';
import type { ArchivoDTO } from '../compartido/vocabulario';
import { BotonIcono, Segmentado } from '../ui';
import { claseDe, desdeArchivoDTO, etiquetaDe, tituloDe } from '../archivos/presentacion';
import { IconoArchivo, Portada } from '../archivos/TarjetaArchivo';
import { urlArchivo } from '../archivos/url';
import { elementoDe, useVistaPrevia, type Autor } from '../archivos/vistaPrevia';

/** Los archivos del chat; se recargan cuando cambia `version` (p. ej. llega un mensaje nuevo). */
export function useArchivos(agentId: string, version: string): ArchivoDTO[] | null {
  const [archivos, setArchivos] = useState<ArchivoDTO[] | null>(null);
  useEffect(() => {
    let vivo = true;
    oficinaApi
      .archivos(agentId)
      .then((r) => vivo && setArchivos(r.archivos))
      .catch(() => vivo && setArchivos([]));
    return () => {
      vivo = false;
    };
  }, [agentId, version]);
  return archivos;
}

/** Abre un archivo de la rejilla: una foto, con las demás fotos de la lista (←/→); un documento, solo. */
function useAbrir(agentId: string, lista: ArchivoDTO[], nombreAgente?: string) {
  const abrir = useVistaPrevia((s) => s.abrir);
  return (a: ArchivoDTO) => {
    const autor: Autor | undefined = a.quien === 'pau' ? { nombre: t('oficinaV2.galeria.deTi') } : nombreAgente ? { nombre: nombreAgente } : undefined;
    const fotos = a.imagen ? lista.filter((x) => x.imagen) : [a];
    abrir({ elementos: fotos.map((x) => elementoDe(agentId, desdeArchivoDTO(x))), indice: Math.max(0, fotos.indexOf(a)), ...(autor ? { autor } : {}), en: a.en });
  };
}

/**
 * Una casilla de la rejilla: la foto, la portada del PDF o el título del informe, con su etiqueta. Las fotos que ya no se
 * pueden abrir (hasta el 24-10 el panel borraba los adjuntos a los 30 días) se ven como «ya no está disponible».
 */
function Casilla({ agentId, a, alAbrir }: { agentId: string; a: ArchivoDTO; alAbrir: () => void }) {
  const [rota, setRota] = useState(false);
  const d = desdeArchivoDTO(a);
  const clase = claseDe(d);
  const titulo = tituloDe(d);
  return (
    <button
      type="button"
      onClick={alAbrir}
      title={titulo}
      aria-label={t('oficinaV2.archivo.abrir', { titulo, detalle: etiquetaDe(d) })}
      data-testid="casilla-archivo"
      className="group relative block aspect-square overflow-hidden bg-oficina-elevada text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-oficina-texto"
    >
      {rota ? (
        <span className="flex h-full w-full flex-col items-center justify-center gap-1 p-1 text-center text-o-mini text-oficina-texto3">
          <ImageOff className="h-5 w-5" aria-hidden />
          {t('oficinaV2.galeria.noDisponible')}
        </span>
      ) : clase === 'imagen' ? (
        <img src={urlArchivo(agentId, a.id)} alt="" loading="lazy" onError={() => setRota(true)} className="h-full w-full object-cover transition-[filter] duration-150 group-hover:brightness-110" />
      ) : clase === 'pdf' ? (
        <Portada agentId={agentId} d={d} alFallar={() => setRota(true)} className="h-full w-full object-cover object-left-top transition-[filter] duration-150 group-hover:brightness-110" />
      ) : clase === 'informe' ? (
        <span className="line-clamp-4 block p-2 text-o-nota font-semibold text-oficina-texto">{titulo}</span>
      ) : (
        <span className="flex h-full w-full items-center justify-center">
          <IconoArchivo d={d} />
        </span>
      )}
      {clase !== 'imagen' && !rota ? (
        <span className="absolute bottom-1.5 left-1.5 rounded-o-s bg-oficina-fondo/80 px-1.5 py-0.5 text-o-mini font-semibold text-oficina-texto2">{etiquetaDe(d)}</span>
      ) : null}
    </button>
  );
}

function Rejilla({ agentId, archivos, alAbrir, className }: { agentId: string; archivos: ArchivoDTO[]; alAbrir: (a: ArchivoDTO) => void; className?: string }) {
  return (
    <div className={cn('grid grid-cols-3 gap-1 overflow-hidden rounded-o-s', className)}>
      {archivos.map((a) => (
        <Casilla key={a.id} agentId={agentId} a={a} alAbrir={() => alAbrir(a)} />
      ))}
    </div>
  );
}

/** La fila de la ficha: lo último (3×2, fotos y documentos) y «Ver todo». */
export function FilaArchivos({ agentId, archivos, alVerTodo }: { agentId: string; archivos: ArchivoDTO[] | null; alVerTodo: () => void }) {
  const abrir = useAbrir(agentId, archivos ?? []);
  if (!archivos?.length) return null;
  return (
    <section data-testid="fila-archivos">
      <div className="mb-1.5 flex items-center justify-between">
        <h3 className="text-o-nota font-semibold text-oficina-texto3">{t('oficinaV2.galeria.titulo')}</h3>
        <button type="button" onClick={alVerTodo} className="text-o-sub font-medium text-oficina-enlace hover:underline">
          {t('oficinaV2.galeria.verTodo', { n: archivos.length })}
        </button>
      </div>
      <Rejilla agentId={agentId} archivos={archivos.slice(0, 6)} alAbrir={abrir} />
    </section>
  );
}

type Filtro = 'todos' | 'pau' | 'agente';

/** La vista entera (ocupa la ficha): filtro y la rejilla de todo, de lo más nuevo a lo más viejo. */
export function Galeria({ agentId, archivos, nombreAgente, alVolver }: { agentId: string; archivos: ArchivoDTO[]; nombreAgente: string; alVolver: () => void }) {
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const lista = archivos.filter((a) => filtro === 'todos' || a.quien === filtro);
  const abrir = useAbrir(agentId, lista, nombreAgente);
  return (
    <section data-testid="galeria" className="flex h-full flex-col">
      <header className="flex shrink-0 items-center gap-1 px-2 pt-3">
        <BotonIcono icono={ChevronLeft} etiqueta={t('oficinaV2.galeria.volver')} onClick={alVolver} />
        <h2 className="text-o-cuerpo font-semibold text-oficina-texto">{t('oficinaV2.galeria.titulo')}</h2>
      </header>
      {/* 04-11 — el filtro, en el control segmentado de Apple (el oro de la oficina es para actuar, no para elegir). */}
      <div className="px-5 py-3">
        <Segmentado
          etiqueta={t('oficinaV2.galeria.filtro')}
          valor={filtro}
          alCambiar={setFiltro}
          opciones={[
            { valor: 'todos', texto: t('oficinaV2.galeria.todos') },
            { valor: 'pau', texto: t('oficinaV2.galeria.tuyos') },
            { valor: 'agente', texto: t('oficinaV2.galeria.suyos') },
          ]}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-6">
        {lista.length === 0 ? <p className="text-o-texto text-oficina-texto3">{t('oficinaV2.galeria.nada')}</p> : <Rejilla agentId={agentId} archivos={lista} alAbrir={abrir} />}
      </div>
    </section>
  );
}
